/** Resolución de idioma del lead (alineado con backend/src/utils/leadLanguage.js). */

const LANGUAGE_LABELS = {
  es: "español",
  ca: "valenciano/catalán",
  en: "inglés",
  de: "alemán",
  fr: "francés",
  nl: "holandés",
  it: "italiano",
  pl: "polaco",
  ru: "ruso",
  uk: "ucraniano",
};

export const LEAD_IDIOMA_OPTIONS = [
  { code: "es", label: "ES · Español" },
  { code: "en", label: "EN · English" },
  { code: "fr", label: "FR · Français" },
  { code: "de", label: "DE · Deutsch" },
  { code: "nl", label: "NL · Nederlands" },
  { code: "ca", label: "CA · Català" },
  { code: "ru", label: "RU · Русский" },
];

export function normalizeLanguageCode(value) {
  const normalized = String(value || "").trim().toLowerCase();
  const mapping = {
    es: "es",
    "es-es": "es",
    español: "es",
    spanish: "es",
    ca: "ca",
    cat: "ca",
    catalan: "ca",
    catalán: "ca",
    valenciano: "ca",
    en: "en",
    eng: "en",
    "en-gb": "en",
    "en-us": "en",
    english: "en",
    ingles: "en",
    inglés: "en",
    anglais: "en",
    england: "en",
    uk: "en",
    gb: "en",
    "united kingdom": "en",
    de: "de",
    german: "de",
    aleman: "de",
    alemán: "de",
    fr: "fr",
    "fr-fr": "fr",
    french: "fr",
    frances: "fr",
    francés: "fr",
    francais: "fr",
    nl: "nl",
    dutch: "nl",
    holandes: "nl",
    holandés: "nl",
    it: "it",
    italian: "it",
    italiano: "it",
    pl: "pl",
    polish: "pl",
    polaco: "pl",
    ru: "ru",
    russian: "ru",
    ruso: "ru",
    uk: "uk",
    ukrainian: "uk",
    ucraniano: "uk",
  };
  return mapping[normalized] || "";
}

export function getLanguageLabel(code, fallback = "") {
  return LANGUAGE_LABELS[code] || fallback || code || "";
}

export function detectLanguageFromMessageHeuristics(text) {
  const sourceText = String(text || "").trim().toLowerCase();
  if (!sourceText) return null;

  const caHint =
    /\b(ascensor|dormitoris|gr[aà]cies|gracies|t[eé] un|te un|quin[s]?|quant[s]?|m'interessa|m'aniria|si us plau)\b/i.test(
      sourceText
    );
  const frHint =
    /\b(est-ce|est ce|c'est|qu'est|merci|bonjour|plage|plages|chambres?|beach|combien)\b/i.test(sourceText) ||
    /\bpr[eè]s\b/i.test(sourceText) ||
    (/[çëêèùûô]/i.test(sourceText) &&
      /\b(la |le |les |un |une |de |du |des |est |ce |ça )\b/i.test(sourceText));

  if (caHint && !frHint) {
    return { code: "ca", label: getLanguageLabel("ca"), source: "message_heuristic" };
  }
  if (frHint) {
    return { code: "fr", label: getLanguageLabel("fr"), source: "message_heuristic" };
  }

  const profiles = {
    ca: ["gràcies", "gracies", "si us plau", "voldria", "m'interessa", "dormitoris", "banys"],
    es: ["gracias", "por favor", "quisiera", "me interesa", "dormitorios", "baños", "hola", "quiero saber"],
    en: ["hello", "thanks", "please", "i would like", "bedrooms", "property", "visit", "price"],
    fr: ["bonjour", "merci", "chambres", "propriété", "visite", "prix"],
    de: ["hallo", "danke", "bitte", "schlafzimmer", "immobilie"],
    nl: ["hallo", "dank", "slaapkamers", "woning"],
    it: ["ciao", "grazie", "camere", "immobile"],
    pl: ["dzień dobry", "dziękuję", "sypialnie"],
  };

  if (/[іїєґ]/i.test(sourceText)) {
    return { code: "uk", label: getLanguageLabel("uk"), source: "message_heuristic" };
  }
  if (/[ыэёъ]/i.test(sourceText) || /спасибо|квартира/i.test(sourceText)) {
    return { code: "ru", label: getLanguageLabel("ru"), source: "message_heuristic" };
  }

  let best = { code: "", score: 0 };
  for (const [code, markers] of Object.entries(profiles)) {
    const score = markers.reduce((acc, marker) => acc + (sourceText.includes(marker) ? 1 : 0), 0);
    if (score > best.score) best = { code, score };
  }
  if (best.score >= 2) {
    return { code: best.code, label: getLanguageLabel(best.code), source: "message_heuristic" };
  }
  return null;
}

/**
 * Idioma efectivo para WhatsApp y UI:
 * 1) mensaje de consulta del interesado, 2) campo idioma, 3) mensaje_original / notas.
 */
export function resolveEffectiveLeadIdioma({
  idioma = null,
  mensaje = null,
  mensaje_original = null,
  notas = null,
} = {}) {
  const consultaText = String(mensaje || "").trim();
  const originalText = String(mensaje_original || notas || "").trim();
  const fromField = normalizeLanguageCode(idioma);

  if (fromField && fromField !== "es") {
    return {
      code: fromField,
      label: getLanguageLabel(fromField),
      source: "lead_field",
      fromMessage: false,
      fieldCode: fromField,
      overridesField: false,
    };
  }

  const fromConsulta = consultaText ? detectLanguageFromMessageHeuristics(consultaText) : null;
  const fromOriginal =
    !consultaText && originalText ? detectLanguageFromMessageHeuristics(originalText) : null;

  if (fromConsulta) {
    return {
      code: fromConsulta.code,
      label: fromConsulta.label,
      source: fromConsulta.source,
      fromMessage: true,
      fieldCode: fromField || null,
      overridesField: Boolean(fromField && fromField !== fromConsulta.code),
    };
  }

  if (fromField) {
    return {
      code: fromField,
      label: getLanguageLabel(fromField),
      source: "lead_field",
      fromMessage: false,
      fieldCode: fromField,
      overridesField: false,
    };
  }

  if (fromOriginal) {
    return {
      code: fromOriginal.code,
      label: fromOriginal.label,
      source: fromOriginal.source,
      fromMessage: true,
      fieldCode: fromField || null,
      overridesField: false,
    };
  }

  return {
    code: "es",
    label: getLanguageLabel("es"),
    source: "default",
    fromMessage: false,
    fieldCode: null,
    overridesField: false,
  };
}

export function formatIdiomaBadge(code) {
  const c = normalizeLanguageCode(code) || "es";
  return c.toUpperCase();
}
